import React, { FC, useRef, useState } from 'react';
import { LoginSchema } from './Login.types';
import {
  changeUserNameFormat,
  checkUsernameType,
  getFormattedUsername,
  isValidEmail,
  isValidSiteId,
  loginCalls,
  removeSpaces
} from '../../services/LoginServiceV2';
import { Spinner } from 'react-bootstrap';
import RichText from '../richtext';
import {
  trackErrorEvent,
  trackEvent,
  trackGADatalayerEvent,
  trackMessageEvent,
  trackUser
} from '../../utils/AnalyticsUtils';
import {
  ERROR,
  ERRORMESSAGE,
  ERRORTYPE,
  FAILED,
  GAEVENT,
  GETTEMPCODE,
  LOGIN,
  LOGINERROR,
  LOGINSUCCESSFUL,
  LOGINUNSUCCESSFUL,
  PASSWORD,
  PASSWORDSUCCESSFUL,
  PASSWORDUNSUCCESSFUL,
  REMMNOTSELECTED,
  REMMSELECTED,
  SUCCESSFULL,
  TYPE
} from '../../Analytics/AnalyticsConstants';
import { API_BASE_BASEPOINT } from '../../services/api-constants';
import {
  DEPRICATED_EMAIL,
  DEPRICATING_EMAIL,
  DEPRICATING_SMS,
  LOGIN_MFA,
  LOGIN_OTP,
  OTP_SMS,
  PW_FORGOT
} from '../../utils/constants';
import Styles from './ReusableLogin.module.scss';

const Login: FC<LoginSchema> = ({
  content,
  changeCurrentView,
  metadata,
  onSuccess,
  loginIcons,
  userID,
  password,
  isProdEnv = true
}): any => {
  const [formValues, setFormValues] = useState({ userName: userID, password: password });
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showInfoText, setShowInfoText] = useState<boolean>(false);
  const [onSubmiError, setOnSubmitError] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isblocked, setIsBlocked] = useState<boolean>(false);
  const [attempts, setAttempts] = useState<number>(1);
  const [prevUsername, setPrevUserName] = useState<string>('');
  const [showInvalidAttempts, setShowInvalidAttempt] = useState<boolean>(false);
  const [technicalError, setTechnicalError] = useState<boolean>(false);
  const [invalidSegment, setInvalidSegment] = useState<boolean>(false);
  const [invalidEmailFormat, setInvalidEmailFormat] = useState<boolean>(false);
  const [invalidNumberFormat, setInvalidNumberFormat] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const controllerRef = useRef<AbortController | null>(null);

  const numberRegex: any = /^[0-9]+$/;

  const handleTextField = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnSubmitError(false);
    setShowInvalidAttempt(false);
    setTechnicalError(false);
    setInvalidSegment(false);
    const fieldValue = e.target.value;
    const fieldId = e.target.id;
    if (fieldId === 'userName') {
      setInvalidEmailFormat(false);
      setInvalidNumberFormat(false);
      setFormValues({ ...formValues, userName: fieldValue });
      if (!fieldValue) {
        setShowInfoText(false);
      } else {
        if (fieldValue && numberRegex.test(fieldValue)) {
          setShowInfoText(true);
        } else setShowInfoText(false);
      }
    }
    if (fieldId === 'password') {
      setFormValues({ ...formValues, password: fieldValue });
    }
  };

  const navigateNext = (nextPress: boolean) => {
    const userNameRegex = /^(?:\+?\d+)$/;
    const { userName, password } = formValues;
    const formatedUserId: string = changeUserNameFormat(userName.replace(/\s/g, ''));
    setFormValues({ ...formValues, userName: formatedUserId });
    if (userName) {
      const formatedUserName = getFormattedUsername(removeSpaces(userName));
      if (isValidSiteId(formatedUserName) || isValidEmail(formatedUserName)) {
        if (nextPress && password !== '') {
          onLogin(formatedUserName, password);
        }
      } else {
        setShowInfoText(false);
        if (userNameRegex.test(formatedUserName)) {
          setInvalidNumberFormat(true);
          trackErrorEvent(ERRORTYPE, ERRORMESSAGE, LOGIN, content?.invalidNumberMessage);
        } else {
          setInvalidEmailFormat(true);
          trackErrorEvent(ERRORTYPE, ERRORMESSAGE, LOGIN, content?.invalidEmailMessage);
        }
      }
    }
  };

  const onLogin = (formatedUserName: any, pswd: any) => {
    setShowLoader(true);
    const userNameType: string | undefined = checkUsernameType(formatedUserName) + ' | ' + PASSWORD;
    loginCalls(
      LOGIN_MFA,
      API_BASE_BASEPOINT?.LOGIN_MFA,
      formatedUserName,
      pswd,
      controllerRef,
      metadata?.lang,
      rememberMe
    )
      .then((res: any) => {
        if (res?.status === 'OK') {
          localStorage.setItem('existingUser', 'true');
          localStorage.setItem('userNameType', userNameType);
          setOnSubmitError(false);
          onSuccess();
          trackEvent(LOGINSUCCESSFUL, PASSWORDSUCCESSFUL, TYPE);
          trackGADatalayerEvent(GAEVENT, LOGIN, {
            result: SUCCESSFULL,
            message_title: '',
            login_type: userNameType,
            selection: rememberMe ? REMMSELECTED : REMMNOTSELECTED
          });
        } else if (
          [DEPRICATING_SMS, DEPRICATING_EMAIL, DEPRICATED_EMAIL, OTP_SMS]?.includes(res?.status)
        ) {
          setShowInfoText(false);
          changeCurrentView(formValues?.userName, res);
        } else if (res?.status === 'ERROR') {
          setTechnicalError(true);
          trackEvent(LOGINUNSUCCESSFUL, PASSWORDUNSUCCESSFUL, TYPE);
          trackMessageEvent(content?.technicalError, LOGIN, LOGINERROR, ERROR);
          trackErrorEvent(ERRORTYPE, ERRORMESSAGE, LOGIN, content?.technicalError);
        } else if (res?.status === 'NOK' && res?.status?.segment) {
          setInvalidSegment(true);
          setShowInfoText(false);
          trackMessageEvent(content?.invalidSegmentError, LOGIN, LOGINERROR, ERROR);
          trackErrorEvent(ERRORTYPE, ERRORMESSAGE, LOGIN, content?.invalidSegmentError);
          trackUser(userNameType);
        } else {
          setOnSubmitError(true);
          setShowInfoText(false);
          checkAttempts();
          trackEvent(LOGINUNSUCCESSFUL, PASSWORDUNSUCCESSFUL, TYPE);
          trackGADatalayerEvent(GAEVENT, LOGIN, {
            result: FAILED,
            message_title: content?.userNameError,
            login_type: userNameType,
            selection: rememberMe ? REMMSELECTED : REMMNOTSELECTED
          });
          trackMessageEvent(content?.userNameError, LOGIN, LOGINERROR, ERROR);
          trackErrorEvent(ERRORTYPE, ERRORMESSAGE, LOGIN, content?.userNameError);
          trackUser(userNameType);
        }
      })
      .catch((err: any) => {
        setOnSubmitError(true);
        setShowInfoText(false);
        console.log(err, '-->error');
        trackEvent(LOGINUNSUCCESSFUL, PASSWORDUNSUCCESSFUL, TYPE);
        trackMessageEvent(content?.userNameError, LOGIN, LOGINERROR, ERROR);
        trackGADatalayerEvent(GAEVENT, LOGIN, {
          result: FAILED,
          message_title: content?.userNameError,
          login_type: userNameType,
          selection: `Remember me | ${rememberMe ? 'Selected' : 'not selected'}`
        });
        trackErrorEvent(ERRORTYPE, ERRORMESSAGE, LOGIN, content?.userNameError);
        trackUser(userNameType);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const checkAttempts = () => {
    if (formValues?.userName === prevUsername) {
      setAttempts(attempts + 1);
    } else {
      setAttempts(1);
      setPrevUserName(formValues?.userName);
    }
    if (attempts >= 2 && attempts < 4) {
      setShowInvalidAttempt(true);
      trackMessageEvent(content?.invalidAttemptError, LOGIN, LOGINERROR, ERROR);
      trackErrorEvent(ERRORTYPE, ERRORMESSAGE, LOGIN, content?.invalidAttemptError);
    } else if (attempts === 4) {
      setShowInvalidAttempt(false);
      setIsBlocked(true);
      const blockedError = content?.loginBlockedError?.content?.[0]?.content?.[0]?.text;
      trackMessageEvent(blockedError, LOGIN, LOGINERROR, ERROR);
      trackErrorEvent(ERRORTYPE, ERRORMESSAGE, LOGIN, blockedError);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && !onSubmiError) {
      navigateNext(true);
    }
  };

  return (
    <>
      <div className={Styles.logintitle}>{content?.title}</div>
      <div className='mt-4' />
      {isblocked && (
        <>
          <div className={Styles.blockedcontent}>
            <span style={{ display: 'inline-flex' }}>
              <div style={{ paddingRight: '10px' }}>
                <img src={loginIcons?.error?.filename} alt='error' width={24} height={24} />{' '}
              </div>
              <RichText content={content?.loginBlockedError} />
            </span>
          </div>
          <div className='mt-4' />
        </>
      )}
      {showInvalidAttempts && (
        <>
          <div className={Styles.warningcontent}>
            <span style={{ display: 'inline-flex' }}>
              <div style={{ paddingRight: '10px' }}>
                <img src={loginIcons?.warning?.filename} alt='error' width={24} height={24} />{' '}
              </div>
              <RichText content={content?.invalidAttemptError} />
            </span>
          </div>
          <div className='mt-4' />
        </>
      )}
      {(technicalError || invalidSegment) && (
        <>
          <div className={`${Styles.warningcontent} ${technicalError && Styles.technicalerror}`}>
            <span style={{ display: 'inline-flex' }}>
              <div style={{ paddingRight: '10px' }}>
                <img src={loginIcons?.warning?.filename} alt='error' width={24} height={24} />{' '}
              </div>
              {technicalError ? (
                <RichText content={content?.technicalError} />
              ) : (
                <RichText content={content?.invalidSegmentError} />
              )}
            </span>
          </div>
          <div className='mt-4' />
        </>
      )}
      <div className='form-floating  w-100'>
        <input
          type='text'
          className={`form-control ${
            (onSubmiError || invalidEmailFormat || invalidNumberFormat) && 'error-border'
          }
           `}
          id='userName'
          placeholder={content?.userNameLabel}
          value={formValues?.userName}
          onChange={handleTextField}
          autoComplete='off'
          disabled={isblocked}
          onBlur={() => navigateNext(false)}
          onKeyDown={onKeyDown}
        />
        <label className={`text-truncate w-100 ${Styles.loginmodalLabel}`}>
          {content?.userNameLabel}
        </label>
        {showInfoText && <p className={Styles.infotooltip}>{content?.infoText}</p>}
        {(invalidEmailFormat || invalidNumberFormat) && (
          <p className={Styles.credentialerror}>
            <img
              src={loginIcons?.alertTriangle?.filename}
              alt='alert'
              className={Styles.warningicon}
            />
            {invalidEmailFormat ? content?.invalidEmailMessage : content?.invalidNumberMessage}
          </p>
        )}
      </div>
      <div className='mt-4' />
      <div className={`form-floating w-100`}>
        <input
          type={showPassword ? 'text' : 'password'}
          className={`form-control ${onSubmiError && 'error-border'} pe-5`}
          id='password'
          placeholder={content?.passwordLabel}
          value={formValues?.password}
          onChange={handleTextField}
          disabled={isblocked}
          onKeyDown={onKeyDown}
        />
        <label className={Styles.loginmodalLabel}>{content?.passwordLabel}</label>
        <div className={Styles.iconcontainer}>
          <span onClick={() => !isblocked && setShowPassword(!showPassword)}>
            {showPassword ? (
              <img src={loginIcons?.eyeoff?.filename} alt='eyeOff' className={Styles.eyeicon} />
            ) : (
              <img src={loginIcons?.eye?.filename} alt='eye' className={Styles.eyeicon} />
            )}
          </span>
        </div>

        {onSubmiError && (
          <p className={Styles.credentialerror}>
            <img
              src={loginIcons?.alertTriangle?.filename}
              alt='alert'
              className={Styles.warningicon}
            />
            {content?.userNameError}
          </p>
        )}
      </div>
      <div className='w-100 d-flex justify-content-between mt-4'>
        {!content?.hideRemMe && (
          <label className={`${Styles.loginmodalrem} me-auto`}>
            <input
              type='checkbox'
              id={'remmberme'}
              onChange={() => setRememberMe(!rememberMe)}
              checked={rememberMe}
            />
            {content?.remLabel}
            <span className={Styles.checkmark}>
              <img
                src={loginIcons?.whiteTickmark?.filename}
                alt='checkmark'
                role='button'
                style={{ marginBottom: '12px', marginLeft: '2px' }}
              />
            </span>
          </label>
        )}
        {content?.forgotPwdLink && (
          <a
            className='text-decoration-none fw-bold ms-auto'
            onClick={() => {
              controllerRef.current && controllerRef.current.abort();
              changeCurrentView(formValues?.userName, { status: PW_FORGOT });
            }}
          >
            {content?.forgotPwdLink}
          </a>
        )}
      </div>
      <div>
        <button
          className={`primarybutton ${Styles.confirmbtn} mt-4`}
          onClick={() => navigateNext(true)}
          disabled={
            onSubmiError ||
            !formValues.password ||
            !formValues.userName ||
            isblocked ||
            invalidEmailFormat ||
            invalidNumberFormat ||
            showLoader
          }
        >
          {showLoader ? (
            <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
          ) : (
            content?.nextButton
          )}
        </button>
      </div>
      {!isProdEnv && (
        <>
          <div className={Styles.horizontalline} />
          <a
            className={`mb-2 text-decoration-none ${Styles.tempcode}`}
            onClick={() => {
              controllerRef?.current?.abort();
              changeCurrentView(formValues?.userName, { status: LOGIN_OTP });
              trackEvent(LOGIN, content?.otpLink, TYPE);
              trackGADatalayerEvent(GAEVENT, GETTEMPCODE, {
                click_text: content?.otpLink
              });
            }}
          >
            {content?.otpLink}
          </a>
        </>
      )}
    </>
  );
};

export default Login;
